import { Fragment, type PropsWithChildren } from 'react';
import { Transition } from '@headlessui/react';

import { useDocumentKeydown } from '@/hooks/use-document-key-down';
import { cls } from '@/utils/cls';

export type SlideOverProps = PropsWithChildren<{
  open?: boolean;
  onClose?: () => void;
  size?: 'xl' | '2xl' | '3xl' | 'full';
  hasDropShadow?: boolean;
  unmount?: boolean;
}>;

export function SlideOver({
  open = false,
  size = 'xl',
  hasDropShadow: hasDropshadow = true,
  onClose,
  children,
  unmount,
}: SlideOverProps) {
  useDocumentKeydown('Escape', function () {
    if (open) {
      onClose?.();
    }
  });

  return (
    <Transition.Root show={open} as={Fragment} unmount={unmount}>
      <div className="absolute inset-0 z-10">
        <Transition.Child
          as={Fragment}
          unmount={unmount}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo={cls('opacity-0', { '!block': unmount === false })}
        >
          <div
            className={cls('absolute inset-0 transition-opacity', {
              'bg-gray-500/75': hasDropshadow,
            })}
            onClick={() => onClose?.()}
          />
        </Transition.Child>

        <div className="pointer-events-none absolute inset-y-0 right-0 flex max-w-full pl-10">
          <Transition.Child
            as={Fragment}
            unmount={unmount}
            enter="transform transition ease-in-out duration-700"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-700"
            leaveFrom="translate-x-0"
            leaveTo={cls('translate-x-full', { '!block': unmount === false })}
          >
            <div
              className={cls('pointer-events-auto w-screen', sizeClasses[size])}
            >
              <div
                className={cls(
                  'flex size-full flex-col overflow-y-scroll border-gray-100 bg-white shadow-xl',
                  { 'border-l': !hasDropshadow },
                )}
              >
                {children}
              </div>
            </div>
          </Transition.Child>
        </div>
      </div>
    </Transition.Root>
  );
}

const sizeClasses = {
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  full: 'max-w-full',
};
